import { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import {
    syncChatfoodPages,
    getChatfoodSyncStatus,
    ChatfoodSyncResult,
} from '@/services/api/chatfood-sync';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';

const TERMINAL_STATUSES = ['completed', 'failed', 'not_found'];

export default function ChatfoodSync() {
    const [token, setToken] = useState('');
    const [userIdsRaw, setUserIdsRaw] = useState('');
    const [result, setResult] = useState<ChatfoodSyncResult | null>(null);
    const [taskId, setTaskId] = useState<string | null>(null);
    const { toast } = useToast();

    const mutation = useMutation({
        mutationFn: () => {
            const user_ids = userIdsRaw
                .split(/[\s,]+/)
                .map((s) => parseInt(s.trim(), 10))
                .filter((n) => !isNaN(n));
            return syncChatfoodPages({ token, user_ids });
        },
        onSuccess: (data) => {
            setResult(null);
            setTaskId(data.task_id);
        },
        onError: (err: any) => {
            toast({
                title: 'Erro no sync',
                description: err?.response?.data?.detail || err.message,
                variant: 'destructive',
            });
        },
    });

    const statusQuery = useQuery({
        queryKey: ['chatfood-sync-status', taskId],
        queryFn: () => getChatfoodSyncStatus(taskId as string),
        enabled: !!taskId,
        refetchInterval: (query) => {
            const status = query.state.data?.status;
            return status && TERMINAL_STATUSES.includes(status) ? false : 2000;
        },
    });

    useEffect(() => {
        const data = statusQuery.data;
        if (!data || !taskId) return;
        if (!TERMINAL_STATUSES.includes(data.status)) return;

        if (data.status === 'completed' && data.result) {
            setResult(data.result);
            toast({
                title: 'Sync concluído',
                description: `${data.result.pages_updated} páginas | ${data.result.vars_saved} variáveis`,
            });
        } else if (data.status === 'failed') {
            toast({
                title: 'Erro no sync',
                description: data.error || 'Falha desconhecida na sincronização',
                variant: 'destructive',
            });
        } else if (data.status === 'not_found') {
            toast({
                title: 'Erro no sync',
                description: 'Task não encontrada ou expirada',
                variant: 'destructive',
            });
        }

        setTaskId(null);
    }, [statusQuery.data, taskId, toast]);

    const isSyncing =
        mutation.isPending ||
        (!!taskId &&
            (!statusQuery.data || !TERMINAL_STATUSES.includes(statusQuery.data.status)));

    const progress = statusQuery.data && !TERMINAL_STATUSES.includes(statusQuery.data.status)
        ? statusQuery.data
        : null;

    return (
        <div className="max-w-lg mx-auto mt-16 p-6 space-y-6">
            <h1 className="text-xl font-semibold">Sync ChatFood</h1>

            <div className="space-y-2">
                <Label htmlFor="token">CHATFOOD_TOKEN</Label>
                <Input
                    id="token"
                    type="password"
                    placeholder="eyJ..."
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                />
            </div>

            <div className="space-y-2">
                <Label htmlFor="user_ids">User IDs (separados por vírgula ou espaço)</Label>
                <Input
                    id="user_ids"
                    placeholder="42, 87, 103"
                    value={userIdsRaw}
                    onChange={(e) => setUserIdsRaw(e.target.value)}
                />
            </div>

            <Button
                onClick={() => mutation.mutate()}
                disabled={isSyncing || !token || !userIdsRaw}
                className="w-full"
            >
                {isSyncing ? (
                    <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sincronizando...
                    </>
                ) : (
                    'Sincronizar'
                )}
            </Button>

            {/* Progresso em tempo real durante o sync */}
            {progress && (
                <div className="rounded-md border p-4 text-sm space-y-1 bg-muted/30">
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-2">
                        Em andamento...
                    </p>
                    <div className="flex justify-between">
                        <span className="text-muted-foreground">Matched</span>
                        <span>{progress.matched ?? 0}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-muted-foreground">Skipped</span>
                        <span>{progress.skipped ?? 0}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-muted-foreground">Páginas atualizadas</span>
                        <span>{progress.pages_updated ?? 0}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-muted-foreground">Variáveis salvas</span>
                        <span>{progress.vars_saved ?? 0}</span>
                    </div>
                </div>
            )}

            {/* Resultado final */}
            {result && (
                <div className="rounded-md border p-4 text-sm space-y-1">
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-2">
                        Concluído
                    </p>
                    <div className="flex justify-between">
                        <span className="text-muted-foreground">Matched</span>
                        <span>{result.matched}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-muted-foreground">Skipped</span>
                        <span>{result.skipped}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-muted-foreground">Páginas atualizadas</span>
                        <span>{result.pages_updated}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-muted-foreground">Variáveis salvas</span>
                        <span>{result.vars_saved}</span>
                    </div>
                    {(result.errors ?? []).length > 0 && (
                        <div className="mt-2 pt-2 border-t space-y-1">
                            <p className="text-muted-foreground font-medium">Erros</p>
                            {(result.errors ?? []).map((e, i) => (
                                <div key={i} className="text-destructive text-xs">
                                    {e.page_id}: {e.error}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
